import {
  Alert,
  Box,
  Snackbar,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import { useEffect, useState } from "react";
import { useCardForm } from "src/hooks/useCardForm";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";

interface SnapIIScoreAssessmentData {
  SS_ID: string;
  CUSER?: string;
  PROCESS_TIME: Date;
  NURSE_NAME?: string;
  MEAN_ARTERIAL_PRESSURE?: string;
  MINIMUM_TEMP?: string;
  PO2_FIO2?: string;
  MINIMUM_PLASMA_PH?: string;
  MULTIPLE_SEIZURES?: string;
  DIUREZ?: string;
  SNAPII_SCORE?: number | string;
  APGAR_SCORE?: string;
  BIRTH_WEIGHT?: string;
  HYPOTROPHY?: string;
  SNAPPEII_SCORE?: number | string;
  OBS_MORTALITY_RATE_OW?: string;
  CALC_MORTALITY_RATE_OW?: string;
  OBS_MORTALITY_RATE_BW?: string;
  CALC_MORTALITY_RATE_BW?: string;
}

interface SnapIIScoreAssessmentProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id?: string) => Promise<void | string | null>;
  handleChange: (field: keyof SnapIIScoreAssessmentData, value: any) => void;
  patientData?: any;
  isSaving?: boolean;
  readOnly?: boolean;
  extraAccess?: boolean;
}

function SnapIIScoreCreate({
  recordId,
  patientId,
  save,
  handleChange: parentHandleChange,
  patientData,
  isSaving,
  readOnly,
  extraAccess,
}: SnapIIScoreAssessmentProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "SNAP_II_SCORE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof SnapIIScoreAssessmentData, value);
      });
    },
  });

  const typedFormData = formData as SnapIIScoreAssessmentData;

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      parentHandleChange(key as keyof SnapIIScoreAssessmentData, value);
    });
  }, [formData, parentHandleChange]);

  const [score, setScore] = useState(0);

  const handleScoreCalculate = () => {
    if (!typedFormData) return;

    const scoreFields = [
      "MEAN_ARTERIAL_PRESSURE_XVALUE",
      "MINIMUM_TEMP_XVALUE",
      "PO2_FIO2_XVALUE",
      "MINIMUM_PLASMA_PH_XVALUE",
      "MULTIPLE_SEIZURES_XVALUE",
      "DIUREZ_XVALUE",
    ];

    const score = scoreFields.reduce((sum, field) => {
      const value = Number((typedFormData as any)[field]) || 0;
      return sum + value;
    }, 0);

    setScore(score);
    handleChange("SNAPII_SCORE", score);
  };

  useEffect(() => {
    handleScoreCalculate();
  }, [
    typedFormData?.MEAN_ARTERIAL_PRESSURE,
    typedFormData?.MINIMUM_TEMP,
    typedFormData?.PO2_FIO2,
    typedFormData?.MINIMUM_PLASMA_PH,
    typedFormData?.MULTIPLE_SEIZURES,
    typedFormData?.DIUREZ,
  ]);

  const [scoreTwo, setScoreTwo] = useState(0);

  const handleScoreTwoCalculate = () => {
    if (!typedFormData) return;

    const scoreFields = [
      "MEAN_ARTERIAL_PRESSURE_XVALUE",
      "MINIMUM_TEMP_XVALUE",
      "PO2_FIO2_XVALUE",
      "MINIMUM_PLASMA_PH_XVALUE",
      "MULTIPLE_SEIZURES_XVALUE",
      "DIUREZ_XVALUE",
      "APGAR_SCORE_XVALUE",
      "BIRTH_WEIGHT_XVALUE",
      "HYPOTROPHY_XVALUE",
    ];

    const scoreTwo = scoreFields.reduce((sum, field) => {
      const value = Number((typedFormData as any)[field]) || 0;
      return sum + value;
    }, 0);

    setScoreTwo(scoreTwo);
    handleChange("SNAPPEII_SCORE", scoreTwo);
  };

  useEffect(() => {
    handleScoreTwoCalculate();
  }, [
    typedFormData?.MEAN_ARTERIAL_PRESSURE,
    typedFormData?.MINIMUM_TEMP,
    typedFormData?.PO2_FIO2,
    typedFormData?.MINIMUM_PLASMA_PH,
    typedFormData?.MULTIPLE_SEIZURES,
    typedFormData?.DIUREZ,
    typedFormData?.APGAR_SCORE,
    typedFormData?.BIRTH_WEIGHT,
    typedFormData?.HYPOTROPHY,
  ]);


  return (
    //AOZMEN;426866;07112024; MADDE ILE SNAP II SKORU FORMU YAPILDI  + //CKAPISIZ;484715;31102025; MADDE ILE SNAP II SKORU FORMU DUZELTILDI
    <>
      <Snackbar
        open={!!error?.open}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error?.message}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => save(typedFormData.SS_ID)}
              loading={isSaving}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid md={6} xs={6} container direction={"column"} spacing={3}>
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("snap2-score.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.createdby")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("snap2-score.createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  onChange={(e) => handleChange("CUSER", e.target.value)}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid md={6} xs={6} sx={{ backgroundColor: "#F4F7FE" }}>
              <ByzMuiTypography isTitle={true}>
                {t("snap2-score.scoreCalculation")}
              </ByzMuiTypography>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.meanArterialPressure")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMcParameterDetailSelect
                  title={t("snap2-score.minPlasmaph")}
                  mcpId={cardData.mcpConfig?.MEAN_ARTERIAL_PRESSURE?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("MEAN_ARTERIAL_PRESSURE", selected);
                    }
                  }}
                  value={selectedMCPs.MEAN_ARTERIAL_PRESSURE}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.MEAN_ARTERIAL_PRESSURE?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.minTemperature")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMcParameterDetailSelect
                  title={t("snap2-score.minTemperature")}
                  mcpId={cardData.mcpConfig?.MINIMUM_TEMP?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("MINIMUM_TEMP", selected);
                    }
                  }}
                  value={selectedMCPs.MINIMUM_TEMP}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.MINIMUM_TEMP?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.po2FiO2")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMcParameterDetailSelect
                  title={t("snap2-score.po2FiO2")}
                  mcpId={cardData.mcpConfig?.PO2_FIO2?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("PO2_FIO2", selected);
                    }
                  }}
                  value={selectedMCPs.PO2_FIO2}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.PO2_FIO2?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.minPlasmaph")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMcParameterDetailSelect
                  title={t("snap2-score.minPlasmaph")}
                  mcpId={cardData.mcpConfig?.MINIMUM_PLASMA_PH?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("MINIMUM_PLASMA_PH", selected);
                    }
                  }}
                  value={selectedMCPs.MINIMUM_PLASMA_PH}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.MINIMUM_PLASMA_PH?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.multipleSeizures")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMcParameterDetailSelect
                  title={t("snap2-score.multipleSeizures")}
                  mcpId={cardData.mcpConfig?.MULTIPLE_SEIZURES?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("MULTIPLE_SEIZURES", selected);
                    }
                  }}
                  value={selectedMCPs.MULTIPLE_SEIZURES}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.MULTIPLE_SEIZURES?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.diuresis")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMcParameterDetailSelect
                  title={t("snap2-score.diuresis")}
                  mcpId={cardData.mcpConfig?.DIUREZ?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("DIUREZ", selected);
                    }
                  }}
                  value={selectedMCPs.DIUREZ}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.DIUREZ?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.snapIIScore")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={score}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid sm={6} md={6} sx={{ backgroundColor: "#F4F7FE" }}>
              <ByzMuiTypography isTitle={true}>
                {t("snap2-score.snapPeScore")}
              </ByzMuiTypography>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.apgarScore")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMcParameterDetailSelect
                  title={t("snap2-score.apgarScore")}
                  mcpId={cardData.mcpConfig?.APGAR_SCORE?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("APGAR_SCORE", selected);
                    }
                  }}
                  value={selectedMCPs.APGAR_SCORE}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.APGAR_SCORE?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.birthWeight")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMcParameterDetailSelect
                  title={t("snap2-score.birthWeight")}
                  mcpId={cardData.mcpConfig?.BIRTH_WEIGHT?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("BIRTH_WEIGHT", selected);
                    }
                  }}
                  value={selectedMCPs.BIRTH_WEIGHT}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.BIRTH_WEIGHT?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.hypotrophy")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMcParameterDetailSelect
                  title={t("snap2-score.hypotrophy")}
                  mcpId={cardData.mcpConfig?.HYPOTROPHY?.mcpId ?? 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("HYPOTROPHY", selected);
                    }
                  }}
                  value={selectedMCPs.HYPOTROPHY}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.HYPOTROPHY?.mcpId ?? 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.snapPeIIScore")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={scoreTwo}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid sm={6} md={6} sx={{ backgroundColor: "#F4F7FE" }}>
              <ByzMuiTypography isTitle={true}>
                {t("snap2-score.combinedByBirthWeight")}
              </ByzMuiTypography>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.observedMortalityRate")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={typedFormData.OBS_MORTALITY_RATE_OW}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.calculatedMortalityRate")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={typedFormData.CALC_MORTALITY_RATE_OW}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid sm={6} md={6} sx={{ backgroundColor: "#F4F7FE" }}>
              <ByzMuiTypography isTitle={true}>
                {t("snap2-score.byBirthWeight")}
              </ByzMuiTypography>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.observedMortalityRate")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={typedFormData.OBS_MORTALITY_RATE_BW}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("snap2-score.calculatedMortalityRate")}
                </ByzMuiTypography>
              </Grid>

              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={typedFormData.CALC_MORTALITY_RATE_BW}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default SnapIIScoreCreate;
